/**
 * Раздел «Экология» панели команды (`/team/ecology`, макет «кабинет АДМИНИСТРАТОРА — раздел
 * Экология» 16.09.2026). Вкладки «Справочник услуг», «Кому что нужно», «Вопросы опросника»,
 * «Правила» (ecology-survey), «Сроки и напоминания» и «Справочник ФККО» (ecology-fkko) правит только
 * администратор; вкладку
 * «Абоненты» (ecology-subscription) ведёт любой сотрудник Центрики, поэтому у сотрудника страница
 * состоит из неё одной. Содержимое каждой вкладки — локальный компонент в `components/`
 * (по образцу каталога программ обучения).
 *
 * Справочник и настройки загружаются один раз на странице и передаются вкладкам: правка в одной
 * вкладке (модалка позиции, матрица, календарь) сразу видна в остальных. Вкладки смонтированы все
 * сразу и скрываются неактивные — несохранённые правки матрицы и календаря не теряются при
 * переключении.
 */
import { useEffect, useState } from 'react'
import { useAuth } from '@/app/auth-context'
import {
  adminApi,
  ecologyApi,
  type EcoCatalogItem,
  type EcoQuestion,
  type EcoRule,
  type EcoSettings,
} from '@/lib/api'
import { getErrorMessage } from '@/lib/errors'
import { PageHead } from '@/design-system/components/layout/page-head'
import { Panel } from '@/design-system/components/layout/panel'
import { Badge } from '@/design-system/components/primitives/badge'
import { Alert } from '@/design-system/components/feedback/alert'
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/design-system/components/primitives/tabs'
import { CatalogTab } from './components/catalog'
import { MatrixTab } from './components/matrix'
import { QuestionsTab } from './components/questions'
import { RulesTab } from './components/rules'
import { TermsTab } from './components/terms'
import { SubscribersTab } from './components/subscribers'
import { FkkoTab } from './components/fkko'

/** Неактивная вкладка остаётся в DOM (forceMount), но скрыта. */
const TAB_CONTENT_CLASS = 'data-[state=inactive]:hidden'

export function TeamEcologyPage() {
  const { context } = useAuth()
  const isAdmin = context?.user.role === 'admin'
  const [items, setItems] = useState<EcoCatalogItem[]>([])
  const [settings, setSettings] = useState<EcoSettings | null>(null)
  const [questions, setQuestions] = useState<EcoQuestion[]>([])
  const [rules, setRules] = useState<EcoRule[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!isAdmin) {
      setLoading(false)
      return
    }
    const controller = new AbortController()
    setLoading(true)
    setError(null)
    Promise.all([
      ecologyApi.listCatalog(controller.signal),
      ecologyApi.getSettings(controller.signal),
      adminApi.ecology.listQuestions(controller.signal),
      adminApi.ecology.listRules(controller.signal),
    ])
      .then(([catalog, s, qs, rs]) => {
        if (controller.signal.aborted) return
        setItems(catalog)
        setSettings(s)
        setQuestions(qs)
        setRules(rs)
      })
      .catch((err) => {
        if (!controller.signal.aborted)
          setError(getErrorMessage(err, 'Не удалось загрузить раздел «Экология»'))
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false)
      })
    return () => controller.abort()
  }, [isAdmin])

  return (
    <div>
      <PageHead
        title={isAdmin ? 'Экология — настройки раздела' : 'Экология — абоненты'}
        subtitle={
          isAdmin
            ? 'Справочник услуг и документов, кому какие документы нужны, опрос и сроки отчётности'
            : 'Клиенты на сопровождении: тариф, инженер, ближайший выезд и план работ'
        }
        actions={<Badge variant="blue">изменения видны клиентам сразу</Badge>}
      />

      {!isAdmin ? (
        <SubscribersTab />
      ) : error ? (
        <Alert variant="danger">{error}</Alert>
      ) : loading || !settings ? (
        <Panel>
          <p className="px-4 py-10 text-center text-txt-secondary">Загрузка…</p>
        </Panel>
      ) : (
        <Tabs defaultValue="catalog">
          <TabsList>
            <TabsTrigger value="catalog">Справочник услуг</TabsTrigger>
            <TabsTrigger value="matrix">Кому что нужно</TabsTrigger>
            <TabsTrigger value="questions">Вопросы опросника</TabsTrigger>
            <TabsTrigger value="rules">Правила</TabsTrigger>
            <TabsTrigger value="terms">Сроки и напоминания</TabsTrigger>
            <TabsTrigger value="fkko">Справочник ФККО</TabsTrigger>
            <TabsTrigger value="subscribers">Абоненты</TabsTrigger>
          </TabsList>
          <TabsContent value="catalog" forceMount className={TAB_CONTENT_CLASS}>
            <CatalogTab items={items} questions={questions} onItemsChange={setItems} />
          </TabsContent>
          <TabsContent value="matrix" forceMount className={TAB_CONTENT_CLASS}>
            <MatrixTab items={items} onItemsChange={setItems} />
          </TabsContent>
          <TabsContent value="questions" forceMount className={TAB_CONTENT_CLASS}>
            <QuestionsTab
              questions={questions}
              rules={rules}
              onQuestionsChange={setQuestions}
              onRulesChange={setRules}
            />
          </TabsContent>
          <TabsContent value="rules" forceMount className={TAB_CONTENT_CLASS}>
            <RulesTab
              rules={rules}
              questions={questions}
              catalog={items}
              onRulesChange={setRules}
            />
          </TabsContent>
          <TabsContent value="terms" forceMount className={TAB_CONTENT_CLASS}>
            <TermsTab
              items={items}
              settings={settings}
              onItemsChange={setItems}
              onSettingsChange={setSettings}
            />
          </TabsContent>
          {/* ФККО и абоненты грузятся своими запросами — вкладки монтируются при открытии. */}
          <TabsContent value="fkko">
            <FkkoTab />
          </TabsContent>
          <TabsContent value="subscribers">
            <SubscribersTab />
          </TabsContent>
        </Tabs>
      )}
    </div>
  )
}
